import { TOEFL_ARENA_HOST } from "../providers/setting";

let bootstrappedEmail: string | null = null;
let bootstrappedName: string | undefined = undefined;
let inflightPromise: Promise<void> | null = null;
let inflightKey: string | null = null;
let latestRequestedKey: string | null = null;

export const isValidEmail = (value: string): boolean => {
    if (!value) return false;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value);
};

export function getBootstrappedEmail(): string | null {
    return bootstrappedEmail;
}

/**
 * Ensures the funnel bootstrap API has been called for the given email+name.
 * - Skips if already bootstrapped for this email with the same (or better) name.
 * - De-duplicates concurrent calls for the same email+name (returns the in-flight promise).
 * - Re-bootstraps if name becomes available after an initial email-only call.
 * - Safe to call from multiple places; only one network request per key will be made.
 */
export async function ensureBootstrapped(
    token: string,
    email: string,
    name?: string,
): Promise<void> {
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedName = (name || '').trim() || undefined;

    if (!token || !isValidEmail(normalizedEmail)) {
        throw new Error('Email and auth token are required for bootstrap');
    }

    // Already bootstrapped for this email with equal-or-better name — nothing to do
    if (bootstrappedEmail === normalizedEmail) {
        // Re-bootstrap only if we now have a name but previously didn't
        if (!normalizedName || bootstrappedName) return;
    }

    const requestKey = `${normalizedEmail}::${normalizedName || ''}`;

    // Track the most recent request
    latestRequestedKey = requestKey;

    // Reuse the in-flight request if it's for the same email+name
    if (inflightPromise && inflightKey === requestKey) {
        return inflightPromise;
    }

    inflightKey = requestKey;
    const promise = (async () => {
        try {
            const response = await fetch(`${TOEFL_ARENA_HOST}/api/funnel/bootstrap`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: normalizedEmail,
                    name: normalizedName,
                }),
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to bootstrap funnel account');
            }

            // Only update state if this is still the most recent request
            if (latestRequestedKey === requestKey) {
                bootstrappedEmail = normalizedEmail;
                bootstrappedName = normalizedName;
            }
        } finally {
            // Only clear in-flight state if we're still the active request
            if (inflightKey === requestKey) {
                inflightPromise = null;
                inflightKey = null;
            }
        }
    })();

    inflightPromise = promise;
    return promise;
}
